// ==========================================
// Data Exports
// Central export for all static data files
// ==========================================

import citiesData from './cities.json'
import defaultsData from './defaults.json'
import assetTypesData from './assetTypes.json'
import type { CityData, LifestyleLevel } from '@/types'

// ==========================================
// Cities Data
// ==========================================

interface CitiesJson {
  cities: CityData[]
  lifestyleMultipliers: Record<LifestyleLevel, number>
  metadata: {
    lastUpdated: string
    currency: string
    baseCityIndex: string
    version: string
  }
}

const typedCitiesData = citiesData as CitiesJson

/**
 * All Indian cities with cost and quality data
 */
export const cities: CityData[] = typedCitiesData.cities

/**
 * Lifestyle multipliers for expense calculations
 */
export const lifestyleMultipliers = typedCitiesData.lifestyleMultipliers

/**
 * Get city by ID
 */
export function getCityById(cityId: string): CityData | undefined {
  return cities.find(city => city.id === cityId)
}

/**
 * Get cities by tier
 */
export function getCitiesByTier(tier: 1 | 2 | 3): CityData[] {
  return cities.filter(city => city.tier === tier)
}

/**
 * Calculate monthly expenses for a city and lifestyle
 */
export function calculateMonthlyCost(
  cityId: string,
  lifestyle: LifestyleLevel,
  housingType: '2BHK' | '3BHK' | 'villa' | 'owned' = '3BHK'
): number {
  const city = getCityById(cityId)
  if (!city) return 0
  
  const multiplier = lifestyleMultipliers[lifestyle]
  
  let housing = 0
  switch (housingType) {
    case '2BHK':
      housing = city.monthlyRent2BHK
      break
    case '3BHK':
      housing = city.monthlyRent3BHK
      break
    case 'villa':
      housing = city.monthlyRentVilla ?? Math.round(city.monthlyRent3BHK * 1.8)  // Use villa rent or estimate
      break
    case 'owned':
      housing = 0  // No rent if owned
      break
  }
  
  const baseCost = 
    housing +
    city.domesticHelp +
    city.utilities +
    city.groceries +
    city.transport +
    city.healthcare +
    city.entertainment
  
  return Math.round(baseCost * multiplier)
}

// ==========================================
// Defaults Data
// ==========================================

interface DefaultsJson {
  profile: {
    currentAge: number
    retirementAge: number
    lifeExpectancy: number
    hasSpouse: boolean
    hasDependentParents: boolean
    currentCountry: string
    targetCountry: string
    exchangeRate: number
  }
  location: {
    primaryCityId: string
    lifestyle: LifestyleLevel
    housingType: string
  }
  incomeExpenses: {
    annualIncomeUSD: number
    monthlyExpensesUSD: number
    expectedExpenseReductionPercent: number
  }
  assumptions: {
    inflationUSA: number
    inflationIndia: number
    medicalInflation: number
    educationInflation: number
    expectedReturnUSA: number
    expectedReturnIndia: number
    fdReturnIndia: number
    safeWithdrawalRate: number
    currencyDepreciation: number
    currencyRiskBuffer: number
  }
  safetyNet: {
    emergencyFundMonths: number
    recommendedTermInsuranceMultiplier: number
    minimumHealthInsuranceINR: number
  }
  savings: {
    annualStepUpPercent: number
    defaultRiskProfile: string
  }
  fireThresholds: {
    lean: { maxAnnualExpenses: number; label: string; description: string; emoji: string }
    regular: { minAnnualExpenses: number; maxAnnualExpenses: number; label: string; description: string; emoji: string }
    fat: { minAnnualExpenses: number; maxAnnualExpenses: number; label: string; description: string; emoji: string }
    obese: { minAnnualExpenses: number; label: string; description: string; emoji: string }
  }
  taxRates: {
    usa: {
      ltcgRate: number
      stcgRate: number
      earlyWithdrawalPenalty: number
      retirement401kWithholding: number
    }
    india: {
      ltcgEquity: number
      stcgEquity: number
      ltcgDebt: number
      surchargeThreshold: number
      rnorPeriodYears: number
    }
  }
  ageConstraints: {
    minAge: number
    maxAge: number
    minRetirementAge: number
    socialSecurityStartAge: number
    retirement401kWithdrawalAge: number
  }
  percentageRanges: {
    inflation: { min: number; max: number }
    returns: { min: number; max: number }
    swr: { min: number; max: number }
    stepUp: { min: number; max: number }
    expenseReduction: { min: number; max: number }
  }
  metadata: {
    version: string
    lastUpdated: string
    notes: string
  }
}

const typedDefaultsData = defaultsData as DefaultsJson

/**
 * All smart defaults for the calculator
 */
export const defaults = typedDefaultsData

/**
 * Default profile values
 */
export const defaultProfile = typedDefaultsData.profile

/**
 * Default assumptions
 */
export const defaultAssumptions = typedDefaultsData.assumptions

/**
 * FIRE type thresholds
 */
export const fireThresholds = typedDefaultsData.fireThresholds

/**
 * Tax rates for USA and India
 */
export const taxRates = typedDefaultsData.taxRates

/**
 * Age constraints for validation
 */
export const ageConstraints = typedDefaultsData.ageConstraints

/**
 * Percentage ranges for validation
 */
export const percentageRanges = typedDefaultsData.percentageRanges

// ==========================================
// Asset Types Data
// ==========================================

interface AssetTypeInfo {
  id: string
  name: string
  description: string
  taxTreatment: string
  icon: string
  repatriationNotes?: string
  earlyWithdrawalPenalty?: boolean
  penaltyAge?: number
  section80C?: boolean
  additionalDeduction?: number
  notes?: string
}

interface AssetTypesJson {
  usa: {
    retirementAccounts: AssetTypeInfo[]
    employerEquity: AssetTypeInfo[]
    investments: AssetTypeInfo[]
    realEstate: AssetTypeInfo[]
    cash: AssetTypeInfo[]
    benefits: AssetTypeInfo[]
  }
  india: {
    retirementAccounts: AssetTypeInfo[]
    investments: AssetTypeInfo[]
    realEstate: AssetTypeInfo[]
    alternatives: AssetTypeInfo[]
    bankAccounts: AssetTypeInfo[]
  }
  liabilityTypes: {
    id: string
    name: string
    description: string
    icon: string
  }[]
  metadata: {
    version: string
    lastUpdated: string
    notes: string
  }
}

const typedAssetTypesData = assetTypesData as AssetTypesJson

/**
 * USA asset types with tax and repatriation info
 */
export const usaAssetTypes = typedAssetTypesData.usa

/**
 * India asset types with tax info
 */
export const indiaAssetTypes = typedAssetTypesData.india

/**
 * Liability/debt types
 */
export const liabilityTypes = typedAssetTypesData.liabilityTypes

/**
 * Get asset type info by ID
 */
export function getAssetTypeInfo(
  country: 'usa' | 'india',
  assetId: string
): AssetTypeInfo | undefined {
  const assetData = country === 'usa' ? usaAssetTypes : indiaAssetTypes
  
  for (const category of Object.values(assetData)) {
    const found = (category as AssetTypeInfo[]).find(asset => asset.id === assetId)
    if (found) return found
  }
  
  return undefined
}

// ==========================================
// Re-export everything
// ==========================================

export {
  citiesData,
  defaultsData,
  assetTypesData,
}

export type {
  AssetTypeInfo,
  CitiesJson,
  DefaultsJson,
  AssetTypesJson,
}

// ==========================================
// School Fees Data
// ==========================================

export {
  SCHOOL_BOARDS,
  CBSE_BASE_FEES,
  getSchoolFee,
  getMonthlySchoolFee,
  getTotalSchoolFees,
  getTotalMonthlySchoolFees,
  formatSchoolFee,
} from './schoolFees'

export type { SchoolBoard, SchoolBoardInfo } from './schoolFees'

// ==========================================
// Benchmarks Data (Task 10)
// ==========================================

export {
  defaultBenchmarks,
  activeBenchmarks,
  getPerformanceTier,
  formatBenchmarkMessage,
  getTierStyles,
} from './benchmarks'

export type {
  BenchmarkThresholds,
  BenchmarkMetric,
  RegionalBenchmarks,
  PerformanceTier,
} from './benchmarks'
